import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';

export default function LearnAdminContentEditor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState<any[]>([]);
  const [selectedModule, setSelectedModule] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    level: '',
    duration_minutes: '',
    version: '',
    content: {}
  });

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    const { data, error } = await supabase
      .from('modules')
      .select('*')
      .order('module_order');
    
    if (error) {
      toast.error('Failed to load modules');
      return;
    }
    
    setModules(data || []);
  };

  const loadModule = (module: any) => {
    setSelectedModule(module);
    setFormData({
      title: module.title,
      level: module.level,
      duration_minutes: module.duration_minutes.toString(),
      version: module.version,
      content: module.content
    });
  };

  const updateModule2Content = async () => {
    setLoading(true);
    
    const module2Content = {
      objectives: [
        'Select an appropriate track based on permissions and confidence: Beginner (No‑Code), Intermediate (GHL), or Advanced',
        'Identify a small, repeatable admin task suitable for early automation',
        'Map the task using the Automation Canvas (Trigger, Inputs, Steps, Decisions, Output, Owner, Guardrails, Metrics, Rollback)',
        'Complete a 10‑minute lab (no‑code or GHL) with a human‑in‑the‑loop approval step',
        'Record a baseline time and define a success metric for ROI reporting'
      ],
      mastery_gate: 'Quiz ≥ 80%',
      prerequisites: 'Module 1',
      privacy_note: 'Do not use production/client data. Use fictional samples only.',
      lesson_script: {
        outcome_first: {
          time: '0:00-0:40',
          content: 'You\'ll reduce admin time by building a tiny, safe workflow: label/triage a common enquiry, draft a short reply, create a task for human approval (HITL), and log the time saved.'
        },
        self_check: {
          time: '0:40-1:20',
          checklist: [
            'I can create email rules/filters in my inbox',
            'I can create templates/canned responses in my inbox',
            'I have permission to use automation tools (Make/Zapier/Power Automate/Apps Script)',
            'Our business uses GoHighLevel (GHL) Workflows'
          ],
          tracks: {
            beginner: 'If only the first two are true → Beginner (No‑Code)',
            intermediate: 'If you also use GHL → Intermediate (GHL)',
            advanced: 'If all are true → Advanced (covered in Pattern modules P1–P5)'
          }
        },
        beginner_track: {
          time: '1:20-6:00',
          goal: '10 minutes: Label common enquiries, insert a safe reply template, and create a human‑approval task — no external tools',
          gmail: [
            'Filter/Label: Settings → Filters → "Has words: booking OR hours OR quote" → label AI‑Triage',
            'Template: Settings → Advanced → enable Templates → compose a 4‑line reply with [TBC] placeholders and tag [HITL‑APPROVE] at the top → save as Template: Enquiry‑Reply',
            'Draft + Task: Open a labelled email → insert template (leave [HITL‑APPROVE]) → More → Add to Tasks → due today + owner',
            'Log: Add a row to the Baseline Timer Sheet (minutes_before, minutes_after)'
          ],
          outlook: [
            'Rule: Home → Rules → subject includes booking/hours/quote → category AI‑Triage',
            'Quick Step: Home → Quick Steps → Reply with Template (include [TBC] + [HITL‑APPROVE])',
            'Task: Follow Up → Add Reminder or Tasks → New Task (due today + owner)',
            'Log: Update the Baseline sheet'
          ],
          safety: 'Never add client PII to prompts; keep [HITL‑APPROVE] until reviewed; ask one clarifying question instead of guessing'
        },
        intermediate_track: {
          time: '6:00-8:00',
          steps: [
            'Trigger: Form submit / new lead → tag AI‑Triage',
            'Note draft: Workflow step Create Note with a 4‑line reply template (include [TBC] + [HITL‑APPROVE])',
            'Task: Create Task (owner, due in 4 hrs); set pipeline stage = "Triage"',
            'Review: Owner edits note, removes [HITL‑APPROVE], sends manually',
            'Log: Minutes_before/after via custom fields or connected Sheet'
          ]
        },
        advanced_track: {
          time: '8:00-9:30',
          patterns: [
            'P1 Email triage → draft reply → task (HITL) (Microsoft/Google/GHL steps and classifier/draft prompts)',
            'P3 Meeting notes → SOP draft (document pipeline)'
          ],
          note: 'Keep HITL for any external send; never auto‑send in the first iteration'
        },
        preflight_safety: {
          time: '9:30-10:50',
          checklist: [
            'Fictional/anonymised data only',
            'Redact PII; no client emails/phone numbers',
            'Human approval before any external message',
            'Keep an audit trail (who/when)',
            'If pasting web text, summarise in your own words first (avoid prompt injection)',
            'Do a tabletop walk‑through: step through Inputs → Decisions → HITL → Output; note gaps; update your Canvas'
          ]
        },
        commit: {
          time: '10:50-12:00',
          content: 'Select one candidate + track; record a baseline (average minutes today). Re‑measure after go‑live; target ≥50% reduction for P1/P3 patterns.'
        }
      },
      lesson_body: {
        what_youll_learn: 'Pick a track, blueprint a small workflow with the Automation Canvas, and complete a safe, 10‑minute lab that already saves time without new software.',
        why_it_matters: 'The no‑code/GHL path removes permission barriers. The Canvas makes your workflow portable to Microsoft/Google/Xero/GHL when you\'re ready.'
      },
      downloadables: {
        automation_canvas: {
          sections: ['Trigger', 'Inputs', 'Steps', 'Decisions', 'Output', 'Owner (HITL)', 'Guardrails', 'Metrics (baseline → target)', 'Rollback']
        },
        preflight_safety_checklist: [
          'Fictional/anonymised data only in tests',
          'Remove PII; keep no client emails/phone numbers',
          'Human approval before any external message',
          'Keep an audit trail (who/when)',
          'Beware prompt injection from pasted web/content',
          'Keep outputs inside your systems (Drive/SharePoint/GHL), not in the AI chat history'
        ],
        baseline_timer_sheet: 'CSV template available in resources'
      },
      prompt_library: {
        canvas_drafting: 'Act as an operations analyst for a NZ SME. Using the bullets below, produce an Automation Canvas with: Trigger, Inputs, Steps, Decisions, Output, Owner (HITL), Guardrails, Metrics, Rollback. Keep each section concise. Ask 3 clarifying questions.',
        drafting_reply: 'You are a drafting assistant. Given [anonymised email text], produce a reply in plain NZ English with: short subject, 3–5 sentence body, and [TBC] for any missing details. Insert the tag [HITL‑APPROVE] at the top to remind the human to review before sending.',
        sop_from_notes: 'Convert these anonymised notes into a one‑page SOP with: Purpose, When, Steps (numbered), Roles, Quality checks. Mark unknowns as [TBC].'
      },
      micro_exercise: {
        steps: [
          'Pick one candidate task from your business',
          'Complete the Track Selector and follow Beginner or GHL steps',
          'Fill the Automation Canvas',
          'Run the Preflight Safety checks',
          'Do a tabletop walk‑through with fictional data',
          'Record your baseline (average minutes today) in the CSV'
        ],
        submission: 'Upload a photo/PDF of your Canvas to /app/capstone (used later)'
      },
      spaced_practice: [
        { after: '24 hours', prompt: 'Which track did you choose and why?' },
        { after: '7 days', prompt: 'What was your baseline vs. actual time saved?' },
        { after: '14 days', prompt: 'Name one guardrail you almost skipped — how did you fix it?' }
      ],
      capstone_link: 'Your Automation Canvas and baseline metrics will feed into the Capstone ROI Calculator in later modules.',
      version_notes: 'v1.1.0 Branching redesign: Beginner (No‑Code), Intermediate (GHL), Advanced deferred to Pattern modules; objective and quiz updated. v1.0.0 Initial release with single‑path guidance.',
      video_url: 'https://www.youtube.com/embed/placeholder'
    };

    // Update module
    const { error: moduleError } = await supabase
      .from('modules')
      .update({
        title: 'Operations Automations',
        level: 'novice',
        duration_minutes: 12,
        version: '1.1.0',
        sector_overlay: 'core',
        wcag_pass: true,
        content: module2Content,
        updated_at: new Date().toISOString()
      })
      .eq('slug', 'operations-automations');

    if (moduleError) {
      toast.error('Failed to update module: ' + moduleError.message);
      setLoading(false);
      return;
    }

    // Update quiz
    const quizQuestions = [
      {
        question: 'If you cannot install Make/Zapier/Power Automate, what should you do?',
        options: [
          'Abandon the lab',
          'Ask IT for admin rights before starting',
          'Complete the no‑code Gmail/Outlook lab using templates + tasks',
          'Copy client emails into a public AI to test'
        ],
        correct_index: 2,
        rationale: 'The no‑code path delivers value without new tools or permissions.'
      },
      {
        question: 'What is the safest default for sending replies in all tracks?',
        options: [
          'Auto‑send replies for speed',
          'Save a draft with [HITL‑APPROVE] and create a task',
          'Send only if the model is confident',
          'Skip approval for common questions'
        ],
        correct_index: 1,
        rationale: 'Human approval before any external send is non‑negotiable.'
      },
      {
        question: 'Which item belongs in the Automation Canvas \'Metrics\' box?',
        options: [
          'Which vendor offers a discount',
          'Baseline minutes and target minutes per task',
          'Owner\'s favourite coffee',
          'The nicest email template'
        ],
        correct_index: 1,
        rationale: 'Measure time saved/error rates; set a concrete target.'
      },
      {
        question: 'Where should the drafted reply live for approval in the Beginner/GHL tracks?',
        options: [
          'Inside the AI chat history only',
          'In a document or note attached to a task for the owner',
          'Printed out for signature',
          'On a public webpage'
        ],
        correct_index: 1,
        rationale: 'Keep artifacts in your systems with audit trail and ownership.'
      },
      {
        question: 'A pasted web snippet changes the AI\'s behaviour. Which risk occurred and what\'s the fix?',
        options: [
          'Hallucination; ask for more creative tone',
          'Prompt injection; strip/summarise the text before use',
          'Bias; add emojis',
          'Math error; switch to a calculator'
        ],
        correct_index: 1,
        rationale: 'Sanitise external text to prevent instruction hijacking.'
      }
    ];

    // Get module id first
    const { data: moduleData } = await supabase
      .from('modules')
      .select('id')
      .eq('slug', 'operations-automations')
      .single();

    if (moduleData) {
      // Check if quiz exists
      const { data: existingQuiz } = await supabase
        .from('quizzes')
        .select('id')
        .eq('module_id', moduleData.id)
        .single();

      if (existingQuiz) {
        // Update existing quiz
        const { error: quizError } = await supabase
          .from('quizzes')
          .update({
            questions: quizQuestions,
            pass_mark: 80
          })
          .eq('id', existingQuiz.id);

        if (quizError) {
          toast.error('Failed to update quiz: ' + quizError.message);
        }
      } else {
        // Create new quiz
        const { error: quizError } = await supabase
          .from('quizzes')
          .insert({
            module_id: moduleData.id,
            questions: quizQuestions,
            pass_mark: 80
          });

        if (quizError) {
          toast.error('Failed to create quiz: ' + quizError.message);
        }
      }
    }

    setLoading(false);
    toast.success('Module 2 content updated successfully!');
    fetchModules();
  };

  const updateModule1Content = async () => {
    setLoading(true);
    
    const module1Content = {
      objectives: [
        'Explain in plain language what modern AI/LLMs do and do not do.',
        'Identify at least 3 high‑value SME use‑cases suitable for early adoption.',
        'Recognise 5 common AI failure modes (hallucination, stale knowledge, bias, prompt injection, math errors).',
        'Apply the SAFE‑NZ rules to avoid privacy/security mistakes (with evidence guidance and copy‑paste safety).',
        'Complete a micro‑exercise turning fictional meeting notes into a draft SOP without using client data.'
      ],
      mastery_gate: 'Quiz ≥ 80%',
      prerequisites: 'None (zero‑barrier)',
      privacy_note: 'Do not use production/client data. Use the fictional notes provided.',
      lesson_script: {
        intro: {
          time: '0:00-0:10',
          content: 'Practical AI for NZ SMEs — Module 1: Capabilities, Limits & Risk Cues'
        },
        quick_mental_model: {
          time: '0:10-0:25',
          content: 'AI is a **drafting helper**. It predicts likely wording based on patterns. Treat outputs as **first drafts** you supervise — not facts or orders.'
        },
        what_ai_is: {
          time: '0:25-2:00',
          points: [
            'AI tools like chatbots are **pattern predictors**. They generate likely next words/steps from large training data.',
            'They are **not** databases of verified facts, nor sentient, nor guaranteed correct.',
            'Use AI as a **junior assistant**: fast drafts, summaries, first passes — supervised by you.'
          ]
        },
        what_ai_can_do: {
          time: '2:00-4:10',
          use_cases: [
            '**Meeting notes → SOP** (standard operating procedure) drafts.',
            '**Email triage**: sort, label, draft replies.',
            '**Lead capture → CRM**: auto‑acknowledge, create task, next‑step checklist.',
            '**Proposal/quote** first draft from a bullet brief.',
            '**Document clean‑up**: grammar, tone, plain‑English rewrites.',
            '**Policy starters**: AI policy, social media policy.',
            '**Checklist generation**: onboarding, safety, quality checks.',
            '**Content repurposing**: blog → email → social posts.',
            '**Data hygiene assists**: detect duplicates; suggest naming conventions.',
            '**FAQ assistants**: internal how‑to answers (not public‑facing yet).'
          ],
          sector_examples: {
            wineries: 'Convert tasting room notes to a next‑day follow‑up checklist.',
            tourism_hospitality: 'Auto‑draft responses to common booking queries.',
            trades_construction: 'Job card bullets → draft quote + safety checklist.',
            professional_services: 'Client intake email → engagement letter draft.'
          }
        },
        limits_failure_modes: {
          time: '4:10-7:30',
          failure_patterns: [
            '**Hallucination** — confident but false specifics.',
            '**Stale knowledge** — models don\'t know "today"; always check dates.',
            '**Prompt injection** — pasted text tries to override your instructions.',
            '**Bias & tone** — stereotyped outputs or wrong tone for NZ audiences.',
            '**Math & logic slips** — small arithmetic or reasoning errors.'
          ],
          copy_paste_safety: [
            'Paste as **plain text** (strip formatting).',
            '**Summarise in your own words** what you pasted (1–2 sentences).',
            'Ask the AI to work **only on your summary**, not the raw paste.',
            'Never paste hidden/system text from the web.'
          ]
        },
        safe_nz_rules: {
          time: '7:30-9:30',
          rules: [
            '**S — Strip PII**: remove personal/client IDs; use **fictional notes** here.',
            '**A — Ask for evidence (when available)**: If the AI is summarising a document you provided, ask it to point to the exact section or quote. For general knowledge answers, treat them as **unverified drafts** and manually check a trusted source.',
            '**F — Fact‑check**: verify critical claims before sending externally.',
            '**E — Export & erase**: save final outputs in Drive/SharePoint; if your prompt included sensitive details, **erase** that chat thread or redact it after you export.',
            '**NZ — Local lens**: write for NZ English and norms; avoid overseas legal advice; keep decisions yours.'
          ]
        },
        inline_micro_lab: {
          time: '9:30-11:30',
          goal: 'Turn **fictional meeting notes** into a clear **SOP draft** you can refine.',
          steps: [
            'Pick **one** scenario (winery / hospitality / trades / professional services) from the **Sample Notes** at the end of this doc.',
            'Paste notes into your AI tool with this prompt:\n```\nAct as an operations assistant. Rewrite the notes into a one‑page SOP in plain English for a New Zealand SME. Include: Purpose, When this applies, Steps (numbered), Roles, Quality checks. Do not invent facts. If details are missing, mark them as [TBC].\n```',
            'Copy the draft into your doc editor; **apply SAFE‑NZ**: remove any stray PII, check for hallucinations, and add [TBC] where needed.',
            'Save your SOP as **SOP‑Draft‑Module1‑YourBusinessName**.'
          ]
        },
        pre_send_check: {
          time: '11:30-12:00',
          checklist: [
            'No client PII added.',
            'Any numbers/dates verified or marked **[TBC]**.',
            'Tone = plain NZ English, not salesy.',
            'If it cites something, I can see where it came from.',
            'Stored in Drive/SharePoint; sensitive prompts erased from chat.'
          ]
        }
      },
      lesson_body: {
        what_youll_learn: 'A clear mental model of what AI is good at, where it fails, and how to use it safely on day one. You\'ll practise converting messy notes into a clean SOP draft using **SAFE‑NZ**.',
        why_it_matters: 'Most SME wins come from better documents and workflows — not big data projects. Use AI where it\'s strong (drafting, reformatting, summarising) and supervise everything else.',
        key_ideas: [
          'AI = pattern prediction → great for drafts, weak for truth.',
          'Start with low‑risk text tasks.',
          'Guard against hallucinations, stale facts, and privacy leaks.',
          'Use **SAFE‑NZ** to keep things tidy and compliant.'
        ]
      },
      spaced_practice: [
        { after: '24 hours', prompt: 'Name two places you\'ll use AI this week that don\'t touch client data.' },
        { after: '7 days', prompt: 'Spot the hallucination: paste a sentence the AI wrote that you edited out — why?' },
        { after: '14 days', prompt: 'Which SAFE‑NZ step do you skip most? Commit to a one‑line fix.' }
      ],
      downloadables: {
        safe_nz_checklist: [
          'Strip PII / client identifiers.',
          'Ask for evidence when available; treat general answers as drafts.',
          'Fact‑check critical claims.',
          'Export outputs into your systems (Drive/SharePoint); erase sensitive chats.',
          'NZ lens: plain‑English, local tone, not legal advice.'
        ],
        glossary: {
          'AI / LLM': 'tools that predict likely text based on patterns.',
          'Hallucination': 'confident but wrong output.',
          'Prompt': 'your instructions to the AI.',
          'Prompt injection': 'hidden text that tries to hijack the AI\'s instructions.',
          'PII': 'personally identifiable info (names, emails, phone numbers).',
          'SOP': 'standard operating procedure.'
        },
        sample_notes: {
          winery: '* 2pm tour fully booked Sat; 3 no‑shows last week.\n* New "Autumn Flight" starts Monday; need next‑day follow‑up email.\n* Stock check Pinot Rosé by Friday; reorder if < 6 cases.\n* Club sign‑ups low; offer 5% voucher when signing up in‑store.',
          hospitality: '* Many calls ask if allergens can be accommodated; need template answer.\n* Weekend wait‑times unclear; staff inconsistent.\n* Social DMs not checked daily; set responsibility.',
          trades: '* Site: 17 Selwyn St; customer prefers Tues/Thurs mornings.\n* Replace switchboard; safety check before energising.\n* Photos required for compliance; upload to shared folder.',
          professional_services: '* New client needs will update; wants plain‑English engagement terms.\n* First meeting 45 min on Zoom; file naming rules missing.\n* Share price list only after conflict check.'
        }
      },
      capstone_link: 'This module\'s micro‑exercise feeds into your later **Capstone (Live Workflow)**. Keep your SOP draft; you\'ll refine and implement one pattern in Module P3.',
      version_notes: [
        '**v1.0.1** Clarity & safety patch: evidence guidance, copy‑paste safety routine, inline micro‑lab, pre‑send check, scenario quiz, explicit export/erase.',
        '**v1.0.0** Initial release.'
      ],
      video_url: 'https://www.youtube.com/embed/placeholder'
    };

    // Update module
    const { error: moduleError } = await supabase
      .from('modules')
      .update({
        title: 'AI Basics for NZ SMEs: Capabilities, Limits & Risk Cues',
        level: 'novice',
        duration_minutes: 12,
        version: '1.0.1',
        sector_overlay: 'core',
        wcag_pass: true,
        content: module1Content,
        updated_at: new Date().toISOString()
      })
      .eq('slug', 'ai-basics-for-smes');

    if (moduleError) {
      toast.error('Failed to update module: ' + moduleError.message);
      setLoading(false);
      return;
    }

    // Update quiz
    const quizQuestions = [
      {
        question: 'What is the most accurate way to think about how modern AI (LLMs) produce answers?',
        options: [
          'They retrieve the single correct fact from a database',
          'They predict likely next words based on patterns in data',
          'They reason like a human expert',
          'They browse the live internet by default'
        ],
        correct_index: 1,
        rationale: 'LLMs are probabilistic next‑token predictors; they can sound confident without verifying truth.'
      },
      {
        question: 'Which situation is MOST risky for hallucination?',
        options: [
          'Rewriting your own meeting notes into an SOP',
          'Summarising a policy you provided',
          'Asking for a precise regulation clause without providing a source',
          'Correcting spelling in your email'
        ],
        correct_index: 2,
        rationale: 'Without a source, the model will invent plausible legal text.'
      },
      {
        question: 'A web page you pasted contains hidden instructions that change the AI\'s behaviour. What failure mode is this?',
        options: [
          'Bias',
          'Prompt injection',
          'Stale knowledge',
          'Math error'
        ],
        correct_index: 1,
        rationale: 'Prompt injection occurs when external text overrides your original instructions.'
      },
      {
        question: 'Which combination best reflects the SAFE‑NZ rules (v1.0.1)?',
        options: [
          'Share PII, Ask for tone, Fact‑check, Export',
          'Strip PII, Ask for evidence (when available), Fact‑check, Export & erase, NZ lens',
          'Save PII, Ask for images, Forward email, NZ lens',
          'Strip PII, Ask for jokes, Fact‑check only if urgent, Export'
        ],
        correct_index: 1,
        rationale: 'SAFE‑NZ now emphasises evidence guidance and erasing sensitive chats.'
      },
      {
        question: 'A draft SOP says \'Email all customers by COB Friday\' but your notes never mentioned emailing customers. What should you do?',
        options: [
          'Send it anyway; it sounds reasonable',
          'Mark it [TBC] and add a comment asking who should be emailed and why',
          'Delete the step quietly',
          'Ask the AI to invent the missing policy'
        ],
        correct_index: 1,
        rationale: 'Flag invented steps explicitly; request the missing decision owner before implementation.'
      }
    ];

    // Get module id first
    const { data: moduleData } = await supabase
      .from('modules')
      .select('id')
      .eq('slug', 'ai-basics-for-smes')
      .single();

    if (moduleData) {
      // Check if quiz exists
      const { data: existingQuiz } = await supabase
        .from('quizzes')
        .select('id')
        .eq('module_id', moduleData.id)
        .single();

      if (existingQuiz) {
        // Update existing quiz
        const { error: quizError } = await supabase
          .from('quizzes')
          .update({
            questions: quizQuestions,
            pass_mark: 80
          })
          .eq('id', existingQuiz.id);

        if (quizError) {
          toast.error('Failed to update quiz: ' + quizError.message);
        }
      } else {
        // Create new quiz
        const { error: quizError } = await supabase
          .from('quizzes')
          .insert({
            module_id: moduleData.id,
            questions: quizQuestions,
            pass_mark: 80
          });

        if (quizError) {
          toast.error('Failed to create quiz: ' + quizError.message);
        }
      }
    }

    setLoading(false);
    toast.success('Module 1 content updated successfully!');
    fetchModules();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/learn/admin/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <Card className="p-6">
          <h1 className="text-3xl font-bold text-brand-navy mb-6">Content Editor</h1>

          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={updateModule1Content}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {loading ? 'Updating...' : 'Update Module 1 Content'}
                </Button>
                <Button 
                  onClick={updateModule2Content}
                  disabled={loading}
                  className="w-full sm:w-auto"
                  variant="outline"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {loading ? 'Updating...' : 'Update Module 2 Content'}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Update modules with complete content including lesson scripts, exercises, and quiz questions.
              </p>
            </div>

            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold mb-4">All Modules</h2>
              <div className="grid gap-4">
                {modules.map((module) => (
                  <Card key={module.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{module.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {module.slug} • {module.level} • {module.duration_minutes} min
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadModule(module)}
                      >
                        View Details
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {selectedModule && (
              <div className="border-t pt-6">
                <h2 className="text-xl font-semibold mb-4">Module Details: {selectedModule.title}</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Content (JSON)</Label>
                    <Textarea
                      value={JSON.stringify(selectedModule.content, null, 2)}
                      readOnly
                      className="font-mono text-xs h-96"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
